// Walking-based scene transition helper.
//
// Usage in scene.create():
//   this.transition = new SceneTransition(this);
//   this.transition.addExitZone({ side: 'left',  toScene: SCENE.OUTSIDE, label: '← Wyjdź' });
//   this.transition.addExitZone({ side: 'right', toScene: SCENE.HOME, label: 'Dom →', onTrigger: async () => {...} });
//
// Then in scene.update():
//   this.transition.checkExits(this.natalka);

import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { UI_DEPTH } from './ySort.js';
import { initialExitState, triggeredState, cancelledState, nextRearmState, shouldCheckExits } from './exitRearm.js';
import { iconText } from './iconText.js';
import { inFeetRect } from './exitRect.mjs';

// Trigger distance from edge. Must be just above WalkController's horizontal bound
// (Natalka clamps to x≈90 from either side), so you have to walk right up to the edge
// to leave — but the zone stays reachable. Exported so scenes whose onTrigger can be
// declined (e.g. MainScene's Podwórko unlock) can nudge the player back out of the zone.
export const EDGE_THRESHOLD = 115;
const LABEL_MARGIN = 80;     // top/bottom labels
const SIDE_MARGIN = 20;      // left/right labels: close to the edge
const SIDE_Y = 240;          // left/right labels: upper side, below the round mute / settings buttons

export class SceneTransition {
  constructor(scene) {
    this.scene = scene;
    this.exits = [];
    // { triggered, rearmAfterLeave } — see exitRearm.js. `triggered` prevents a double-fire
    // during a transition; `rearmAfterLeave` blocks a declined exit from instantly re-firing
    // while she is still standing in the zone.
    this._state = initialExitState();
  }

  addExitZone({ side, toScene, label, onTrigger = null, data = null, rect = null, labelAt = null }) {
    this.exits.push({ side, toScene, onTrigger, data, rect });
    this._drawLabel(side, label, labelAt);
  }

  _drawLabel(side, label, labelAt = null) {
    const style = {
      fontFamily: 'system-ui', fontSize: '15px', color: '#fff',
      backgroundColor: '#00000099', padding: { x: 7, y: 4 }, fontStyle: 'bold',
    };
    let t = null;
    if (side === 'left') {
      t = iconText(this.scene, SIDE_MARGIN, SIDE_Y, label, style).setOrigin(0, 0.5);
    } else if (side === 'right') {
      t = iconText(this.scene, GAME_WIDTH - SIDE_MARGIN, SIDE_Y, label, style).setOrigin(1, 0.5);
    } else if (side === 'top') {
      t = iconText(this.scene, GAME_WIDTH / 2, LABEL_MARGIN, label, style).setOrigin(0.5, 0);
    } else if (side === 'bottom') {
      t = iconText(this.scene, GAME_WIDTH / 2, GAME_HEIGHT - 15, label, style).setOrigin(0.5, 1);
    } else if (side === 'rect' && labelAt) {
      // A gate in the world (Podwórko's back fence): the label stands over it and scrolls with it.
      t = iconText(this.scene, labelAt.x, labelAt.y, label, style).setOrigin(0.5, 1);
    }
    // Subtle hint: above y-sorted gameplay sprites (e.g. garden crops) but semi-transparent.
    t?.setDepth(UI_DEPTH).setAlpha(0.7);
  }

  // Is this position inside `ex`'s trigger band?
  _inZone(ex, natalka) {
    // Use looser threshold for left/right (entire screen height to walk into),
    // tighter for top/bottom (must walk to actual edge).
    const VERTICAL_THRESHOLD = 10;
    if (ex.side === 'left')   return natalka.x <= EDGE_THRESHOLD;
    if (ex.side === 'right')  return natalka.x >= GAME_WIDTH - EDGE_THRESHOLD;
    if (ex.side === 'top')    return natalka.y <= VERTICAL_THRESHOLD;
    if (ex.side === 'bottom') return (natalka.feetY ?? natalka.y + 120) >= GAME_HEIGHT - VERTICAL_THRESHOLD;
    if (ex.side === 'rect')   return inFeetRect(ex.rect, natalka.x, natalka.feetY ?? natalka.y + 120);
    return false;
  }

  checkExits(natalka) {
    const hit = this.exits.find((ex) => this._inZone(ex, natalka)) || null;
    // Re-arm a previously declined exit as soon as she is outside EVERY zone.
    this._state = nextRearmState(this._state, hit !== null);
    if (!shouldCheckExits(this._state, hit !== null) || !hit) return;

    this._state = triggeredState(this._state);
    if (hit.onTrigger) {
      hit.onTrigger().then((ok) => {
        if (ok !== false) {
          this.scene.scene.start(hit.toScene, hit.data || undefined);
        } else {
          // Cancelled/failed: she is still standing in the zone (input was locked by the
          // modal), so only offer again once she has walked out of every exit zone.
          this._state = cancelledState(this._state);
        }
      });
    } else {
      this.scene.scene.start(hit.toScene, hit.data || undefined);
    }
  }
}
